import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ListChecks, X } from 'lucide-react';
import { shortlistApi, jobsApi } from '../../../shared/services/api';
import { useAuth } from '../../auth';
import { canWorkJob } from '../../../shared/utils/permissions';

const SCORE_COLOR = (s) => {
  if (!s && s !== 0) return 'var(--text-muted)';
  return s >= 70 ? 'var(--green)' : s >= 50 ? 'var(--accent)' : 'var(--yellow)';
};

// ── Compare Modal ──────────────────────────────────────────────────────────
function CompareModal({ candidates, jobId, onClose }) {
  const [selected, setSelected] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 4 ? [...prev, id] : prev);
  };

  const handleCompare = async () => {
    if (selected.length < 2) return alert('Select at least 2 candidates to compare.');
    setLoading(true);
    try {
      const data = await shortlistApi.compare(jobId, selected);
      setCompareData(data);
    } catch (e) {
      alert('Compare failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 1000, padding: 24, overflowY: 'auto',
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 16, padding: 28,
        width: '100%', maxWidth: 900, border: '1px solid var(--border)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        marginTop: 24, marginBottom: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>Compare Candidates</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Select 2–4 candidates for side-by-side comparison</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}><X size={20} strokeWidth={2} /></button>
        </div>

        {!compareData ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {candidates.map((c, i) => (
                <label key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                  border: `2px solid ${selected.includes(c.id) ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 8, cursor: 'pointer',
                  background: selected.includes(c.id) ? 'rgba(37,99,235,0.06)' : 'var(--bg-surface)',
                  transition: 'all 0.15s',
                }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(c.id)}
                    onChange={() => toggle(c.id)}
                    style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                  />
                  <span style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: 12, minWidth: 28 }}>#{i + 1}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.current_role} {c.current_company ? `@ ${c.current_company}` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: SCORE_COLOR(c.final_score), fontFamily: 'var(--font-mono)' }}>{c.final_score}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>final</div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCompare} disabled={selected.length < 2 || loading} style={{ flex: 2 }}>
                {loading ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Comparing...</> : `Compare ${selected.length} Candidate${selected.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Comparison table */}
            <div style={{ overflowX: 'auto', marginBottom: 20 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', width: 140 }}>Attribute</th>
                    {compareData.candidates.map(c => (
                      <th key={c.id} style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '2px solid var(--border)', fontSize: 13 }}>
                        <Link to={`/candidates/${c.id}`} style={{ color: 'var(--accent)', fontWeight: 700 }}>{c.name}</Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'resume_score', label: 'Resume Score', render: (v) => <span style={{ color: SCORE_COLOR(v), fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{v ?? '—'}</span> },
                    { key: 'interview_score', label: 'Interview Score', render: (v) => <span style={{ color: SCORE_COLOR(v), fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{v ?? '—'}</span> },
                    { key: 'final_score', label: 'Final Score', render: (v) => <span style={{ color: SCORE_COLOR(v), fontWeight: 800, fontFamily: 'var(--font-mono)', fontSize: 18 }}>{v ?? '—'}</span> },
                    { key: 'total_experience_years', label: 'Experience', render: (v) => v ? `${v} years` : '—' },
                    { key: 'notice_period', label: 'Notice Period', render: (v) => v || '—' },
                    { key: 'current_ctc', label: 'Current CTC', render: (v) => v || '—' },
                    { key: 'expected_ctc', label: 'Expected CTC', render: (v) => v || '—' },
                    { key: 'location', label: 'Location', render: (v) => v || '—' },
                    { key: 'resume_score_band', label: 'Score Band', render: (v) => v ? <span className={`badge ${v === 'Strong Match' ? 'badge-green' : v === 'Good Match' ? 'badge-blue' : 'badge-yellow'}`}>{v}</span> : '—' },
                  ].map(row => (
                    <tr key={row.key} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '9px 12px', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{row.label}</td>
                      {compareData.candidates.map(c => (
                        <td key={c.id} style={{ padding: '9px 12px', textAlign: 'center', fontSize: 13 }}>
                          {row.render(c[row.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Skills comparison */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Skills</div>
              <div className="r-compare-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${compareData.candidates.length}, 1fr)`, gap: 12 }}>
                {compareData.candidates.map(c => (
                  <div key={c.id}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 6 }}>{c.name}</div>
                    <div className="tags-wrap">
                      {(c.skills || []).slice(0, 8).map(s => <span key={s} className="tag" style={{ fontSize: 11 }}>{s}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setCompareData(null)} style={{ flex: 1 }}>Back</button>
              <button className="btn btn-primary" onClick={onClose} style={{ flex: 1 }}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
const REJECTED_STAGES = ['rejected_resume', 'rejected_interview', 'rejected_final'];

const STATUS_TABS = [
  { value: 'in_review', label: 'In Review', level: 'Still in process', color: 'var(--accent)' },
  { value: 'selected', label: 'Selected', level: 'Selected in the final round', color: 'var(--green)' },
  { value: 'on_hold', label: 'On hold', level: 'Put on hold', color: 'var(--yellow)' },
  { value: 'rejected', label: 'Rejected', level: 'Rejected at any round', color: 'var(--red)' },
];

// Where each candidate stands, shown on their card.
const STAGE_BADGE = {
  applied: { label: 'Applied', cls: 'badge-gray' },
  resume_scored: { label: 'Resume scored', cls: 'badge-gray' },
  resume_reviewed: { label: 'Resume reviewed', cls: 'badge-blue' },
  interview_scheduled: { label: 'AI interview scheduled', cls: 'badge-blue' },
  interview_in_progress: { label: 'AI interview in progress', cls: 'badge-blue' },
  interview_completed: { label: 'AI interview done', cls: 'badge-blue' },
  second_round_invited: { label: 'Selected for next round', cls: 'badge-blue' },
  second_round_scheduled: { label: 'Final round scheduled', cls: 'badge-blue' },
  second_round_completed: { label: 'Final round done', cls: 'badge-blue' },
  completed: { label: 'Selected', cls: 'badge-green' },
  rejected_resume: { label: 'Rejected at resume', cls: 'badge-red' },
  rejected_interview: { label: 'Rejected at AI interview', cls: 'badge-red' },
  rejected_final: { label: 'Rejected in final round', cls: 'badge-red' },
};

function CardStageBadge({ candidate: c }) {
  const hold = c.recruiter_decision === 'Hold'
    && !REJECTED_STAGES.includes(c.pipeline_stage)
    && c.pipeline_stage !== 'completed';
  const badge = hold
    ? { label: 'On hold', cls: 'badge-yellow' }
    : STAGE_BADGE[c.pipeline_stage];
  if (!badge) return null;
  return <span className={`badge ${badge.cls}`}>{badge.label}</span>;
}

export default function Shortlist() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(jobId || '');
  // Generating or sending a job's shortlist is for the person it is assigned to.
  const mayRecruit = canWorkJob(user, jobs.find(j => j.id === selectedJob));
  // Which list: in review, selected, on hold, or rejected.
  const [status, setStatus] = useState('in_review');
  const [shortlist, setShortlist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);

  useEffect(() => {
    jobsApi.list().then(setJobs);
  }, []); // eslint-disable-line

  const loadList = (quiet = false) => {
    if (!quiet) setLoading(true);
    const load = selectedJob ? shortlistApi.get(selectedJob, status) : shortlistApi.getAll(status);
    return load
      .then(data => {
        setShortlist(data);
      })
      .catch(() => setShortlist(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setNotifyResult(null);
    loadList();
  }, [selectedJob, status]); // eslint-disable-line

  const handleGenerate = async () => {
    const job = jobs.find(j => j.id === selectedJob);
    const topN = job?.shortlist_size || 10;
    setGenerating(true);
    try {
      await shortlistApi.generate(selectedJob, topN);
      await loadList(true);
    } catch (e) { alert(e.message); }
    finally { setGenerating(false); }
  };

  const handleNotifyManager = async () => {
    setNotifying(true);
    setNotifyResult(null);
    try {
      const result = await shortlistApi.notifyManager(selectedJob);
      setNotifyResult(result);
    } catch (e) {
      alert('Notify failed: ' + e.message);
    } finally {
      setNotifying(false);
    }
  };

  const selectedJobObj = jobs.find(j => j.id === selectedJob);
  const resumeWeight = shortlist?.resume_weight ?? selectedJobObj?.resume_weight ?? 0.4;
  const interviewWeight = shortlist?.interview_weight ?? selectedJobObj?.interview_weight ?? 0.6;

  return (
    <div>
      {showCompare && shortlist && (
        <CompareModal
          candidates={shortlist.candidates || []}
          jobId={selectedJob}
          onClose={() => setShowCompare(false)}
        />
      )}

      <div className="page-header">
        <div>
          <div className="page-title">Final Shortlist</div>
          <div className="page-subtitle">Every candidate, grouped by where they stand</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="form-select" value={selectedJob} onChange={e => setSelectedJob(e.target.value)} style={{ width: 240 }}>
            <option value="">All jobs</option>
            {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>

          {selectedJob && status === 'in_review' && shortlist?.total_shortlisted > 0 && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCompare(true)} title="Compare candidates side-by-side">
                Compare
              </button>
              {mayRecruit && <button
                className="btn btn-secondary btn-sm"
                onClick={handleNotifyManager}
                disabled={notifying}
                title="Notify hiring manager"
              >
                {notifying ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Notifying...</> : 'Notify Manager'}
              </button>}
            </>
          )}

          {selectedJob && mayRecruit && (
            <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
              {generating
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Generating...</>
                : 'Generate Shortlist'}
            </button>
          )}
        </div>
      </div>

      {/* Notify result */}
      {notifyResult && (
        <div style={{
          background: 'rgba(5,150,105,0.08)', border: '1px solid var(--green)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          fontSize: 13, color: 'var(--text-secondary)',
        }}>
          <div style={{ fontWeight: 600, color: 'var(--green)', marginBottom: 6 }}>Hiring Manager Notified</div>
          <div><strong>To:</strong> {notifyResult.to}</div>
          <div><strong>Subject:</strong> {notifyResult.subject}</div>
          {notifyResult.note && <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>{notifyResult.note}</div>}
        </div>
      )}

      {/* Pipeline threshold info */}
      {selectedJobObj && (
        <div style={{
          background: 'rgba(88,166,255,0.06)',
          border: '1px solid rgba(88,166,255,0.2)',
          borderRadius: 8,
          padding: '10px 16px',
          marginBottom: 20,
          display: 'flex',
          gap: 24,
          fontSize: 13,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>Resume threshold: </span>
            <strong style={{ color: 'var(--accent)' }}>{selectedJobObj.resume_score_threshold ?? 75}/100</strong>
          </span>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>Interview threshold: </span>
            <strong style={{ color: 'var(--purple)' }}>{selectedJobObj.interview_score_threshold ?? 60}/100</strong>
          </span>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>Shortlist size: </span>
            <strong style={{ color: 'var(--green)' }}>Top {selectedJobObj.shortlist_size ?? 10}</strong>
          </span>
          <span>
            <span style={{ color: 'var(--text-muted)' }}>Final score weights: </span>
            <strong style={{ color: 'var(--accent)' }}>Resume {Math.round(resumeWeight * 100)}%</strong>
            {' + '}
            <strong style={{ color: 'var(--purple)' }}>Interview {Math.round(interviewWeight * 100)}%</strong>
          </span>
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div className="spinner" style={{ width: 28, height: 28 }} />
        </div>
      )}

      {shortlist && (
        <>
          <div role="tablist" aria-label="Which candidates to show"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            {STATUS_TABS.map(t => {
              const active = status === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className="stat-card"
                  onClick={() => setStatus(t.value)}
                  style={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    font: 'inherit',
                    width: '100%',
                    border: `1.5px solid ${active ? t.color : 'var(--border)'}`,
                    background: active
                      ? `color-mix(in srgb, ${t.color} 12%, var(--bg-card))`
                      : 'var(--bg-card)',
                    boxShadow: active
                      ? `0 0 0 3px color-mix(in srgb, ${t.color} 22%, transparent)`
                      : undefined,
                  }}
                >
                  <div className="stat-label" style={{ color: active ? t.color : undefined }}>
                    {t.label}
                  </div>
                  <div className="stat-value" style={{ color: active ? t.color : 'var(--text-muted)' }}>
                    {shortlist.counts?.[t.value] ?? '—'}
                  </div>
                  <div style={{ fontSize: 12, color: active ? t.color : 'var(--text-muted)', marginTop: 4 }}>
                    {t.level}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {!loading && shortlist && shortlist.total_shortlisted > 0 && (
        <>

          {/* Candidate Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {shortlist.candidates.map((c, i) => (
              <div key={c.id} className="card" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* Rank Badge */}
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: i === 0 ? 'rgba(63,185,80,0.2)' : i === 1 ? 'rgba(88,166,255,0.2)' : i === 2 ? 'rgba(210,153,34,0.2)' : 'var(--bg-hover)',
                  border: `2px solid ${i === 0 ? 'var(--green)' : i === 1 ? 'var(--accent)' : i === 2 ? '#d29922' : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 16,
                  color: i === 0 ? 'var(--green)' : i === 1 ? 'var(--accent)' : i === 2 ? '#d29922' : 'var(--text-muted)'
                }}>#{i + 1}</div>

                {/* Main Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <Link to={`/candidates/${c.id}`} style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', textDecoration: 'none' }}>
                        {c.name}
                      </Link>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                        {c.current_role} {c.current_company ? `@ ${c.current_company}` : ''} {c.location ? `· ${c.location}` : ''}
                      </div>
                      {!selectedJob && c.job_title && (
                        <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 3, fontWeight: 600 }}>
                          Applied for {c.job_title}
                        </div>
                      )}
                    </div>
                    {/* Final score once there is one; before the interview the
                        resume score is the only score, so show that, named. */}
                    {(c.final_score ?? c.resume_score) != null && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontSize: 26, fontWeight: 800,
                          fontFamily: 'var(--font-mono)',
                          color: SCORE_COLOR(c.final_score ?? c.resume_score)
                        }}>{c.final_score ?? c.resume_score}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {c.final_score != null ? 'final score' : 'resume score'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Details — only what the candidate actually has, so a card is
                      never a row of dashes. */}
                  <div style={{ display: 'flex', gap: '6px 20px', marginBottom: 10, flexWrap: 'wrap' }}>
                    <ScorePill label="Resume" score={c.resume_score} band={c.resume_score_band} />
                    {c.interview_score != null
                      ? <ScorePill label="Interview" score={c.interview_score} />
                      : <Detail label="Interview" value="Not taken yet" muted />}
                    {c.total_experience_years != null && (
                      <Detail label="Experience" value={`${c.total_experience_years} ${c.total_experience_years === 1 ? 'yr' : 'yrs'}`} />
                    )}
                    {c.notice_period && <Detail label="Notice" value={c.notice_period} />}
                    {c.current_ctc && <Detail label="Current CTC" value={c.current_ctc} />}
                    {c.expected_ctc && <Detail label="Expected CTC" value={c.expected_ctc} />}
                    {c.email && <Detail label="Email" value={c.email} />}
                    {c.phone && <Detail label="Phone" value={c.phone} />}
                  </div>

                  {REJECTED_STAGES.includes(c.pipeline_stage) && c.archived_reason && (
                    <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>
                      {c.archived_reason.replace(/^Auto-archived:\s*/, 'Rejected: ')}
                    </div>
                  )}

                  {/* Action Row */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Read-only. Decisions are made on the candidate's profile,
                        where the pipeline, scores and recordings sit beside them;
                        this page is for seeing the result. The "x" that used to
                        sit on this badge only hid the decision in the browser and
                        never reached the server, so it came back on reload. */}
                    <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center' }}>
                      {/* From the pipeline stage, the same field that picks the tab,
                          so the badge and the tab always agree. */}
                      <CardStageBadge candidate={c} />
                      <Link to={`/candidates/${c.id}`} className="btn btn-ghost btn-sm">View Profile</Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && (!shortlist || shortlist.total_shortlisted === 0) && (
        <div className="empty-state">
          <div className="empty-state-icon"><ListChecks size={40} strokeWidth={1.5} /></div>
          <div className="empty-state-title">
            {status === 'selected' ? 'No selected candidates yet'
              : status === 'rejected' ? 'No rejected candidates'
              : status === 'on_hold' ? 'No candidates on hold'
              : 'Nobody is in review'}
          </div>
          <div className="empty-state-text">
            {status === 'selected'
              ? 'Candidates appear here once they are selected in the final round.'
              : status === 'rejected'
                ? 'Candidates rejected at any round appear here.'
                : status === 'on_hold'
                  ? 'Candidates put on hold appear here.'
                  : 'Everyone still in process appears here.'}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, muted }) {
  return (
    <div style={{ fontSize: 12 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}: </span>
      <span style={{ color: muted ? 'var(--text-muted)' : 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}

function ScorePill({ label, score, band }) {
  return (
    <div style={{ fontSize: 12 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}: </span>
      <span style={{ color: SCORE_COLOR(score), fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
        {score ?? '—'}
      </span>
      {band && <span style={{ color: 'var(--text-muted)', fontSize: 10, marginLeft: 3 }}>({band})</span>}
    </div>
  );
}
